import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Monitor, Search, Wifi, WifiOff, AlertTriangle, 
  Download, RefreshCw, ChevronRight, RotateCcw, AlertCircle,
  Plus, Database, CloudOff, Cloud, Server, Printer, Smartphone, Laptop, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_COLORS = {
  'Online': 'bg-green-100 text-green-700',
  'Offline': 'bg-gray-100 text-gray-500',
  'Active': 'bg-blue-100 text-blue-700',
  'Available': 'bg-green-100 text-green-700',
  'Checked Out': 'bg-orange-100 text-orange-700',
  'checked_out': 'bg-orange-100 text-orange-700',
  'available': 'bg-green-100 text-green-700',
  'Retired': 'bg-red-100 text-red-600',
  'In Repair': 'bg-yellow-100 text-yellow-700',
  'Unknown': 'bg-gray-100 text-gray-400',
};

const ASSET_TYPE_ICONS = {
  'Computer': Laptop,
  'Laptop': Laptop,
  'Desktop': Monitor,
  'Server': Server,
  'Printer': Printer,
  'Monitor': Monitor,
  'Network': Server,
  'Phone': Smartphone,
  'Tablet': Smartphone,
  'Peripheral': Monitor,
  'Software': Monitor,
  'Furniture': Monitor,
  'Other': Monitor,
};

const AssetsPage = () => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: 'all', department: 'all', type: 'all', source: 'all' });
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [newAsset, setNewAsset] = useState({
    name: '', asset_type: 'Computer', serial: '', manufacturer: '',
    model: '', department: '', location: '', assigned_to: '', notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setSelectedAssets([]);
    try {
      const [assetsRes, statusRes, statsRes] = await Promise.all([
        api.getAssets({ include_live: false, limit: 500 }),
        api.getSyncStatus(),
        api.getAssetStats()
      ]);
      
      setAssets(assetsRes.data.assets || []);
      setSyncStatus(statusRes.data);
      setInventory(statsRes.data.inventory || []);
      
    } catch (err) {
      console.error('Failed to fetch assets:', err);
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (fullSync = false) => {
    setSyncing(true);
    try {
      const res = await api.syncAssets(fullSync);
      toast.success(res.data.message || 'Sync started');
      
      // Refresh after a delay to let sync complete
      setTimeout(fetchData, 2000);
    } catch (err) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleImport = async () => {
    setSyncing(true);
    try {
      const res = await api.importAssetsFromAction1();
      if (res.data.success) {
        toast.success(`Imported ${res.data.imported} assets from Action1`);
        fetchData();
      } else {
        toast.error(res.data.error || 'Import failed');
      }
    } catch (err) {
      toast.error('Import failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleAddAsset = async () => {
    try {
      const result = await api.createAsset(newAsset);
      toast.success(`Asset created: ${result.data.name}`);
      setShowAddDialog(false);
      setNewAsset({
        name: '', asset_type: 'Laptop', serial: '', manufacturer: '',
        model: '', department: '', location: '', assigned_to: '', notes: ''
      });
      fetchData();
    } catch (err) {
      toast.error('Failed to create asset');
    }
  };

  // Toggle asset selection
  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  // Select/deselect all visible assets
  const toggleSelectAll = () => {
    if (selectedAssets.length === filteredAssets.length) {
      setSelectedAssets([]);
    } else {
      setSelectedAssets(filteredAssets.map(a => a.asset_id));
    }
  };

  // Bulk delete
  const handleBulkDelete = async (permanent = false) => {
    const action = permanent ? 'permanently delete' : 'retire';
    if (!window.confirm(`Are you sure you want to ${action} ${selectedAssets.length} asset(s)?`)) return;
    
    setDeleting(true);
    try {
      const res = await api.bulkDeleteAssets(selectedAssets, permanent);
      toast.success(`${res.data.deleted} asset(s) ${permanent ? 'deleted' : 'retired'}`);
      setSelectedAssets([]);
      fetchData();
    } catch (err) {
      toast.error('Failed to delete assets');
    } finally {
      setDeleting(false);
    }
  };

  // Calculate stats
  const stats = {
    total: assets.length,
    online: assets.filter(a => a.effective_status === 'Online').length,
    offline: assets.filter(a => a.effective_status === 'Offline').length,
    manual: assets.filter(a => a.source === 'manual').length,
    action1: assets.filter(a => a.action1_id).length,
    needsUpdates: assets.filter(a => (a.action1_missing_updates?.critical || 0) > 0).length,
    needsReboot: assets.filter(a => a.action1_reboot_required === 'Yes').length,
  };

  // Get unique values for filters
  const departments = [...new Set(assets.map(a => a.department).filter(Boolean))].sort();
  const assetTypes = [...new Set(assets.map(a => a.asset_type).filter(Boolean))].sort();

  // Filter assets
  const filteredAssets = assets.filter(a => {
    if (search) {
      const searchLower = search.toLowerCase();
      const searchable = `${a.name} ${a.serial} ${a.ip_address} ${a.assigned_to} ${a.department} ${a.manufacturer}`.toLowerCase();
      if (!searchable.includes(searchLower)) return false;
    }
    
    // Handle status filter including Available/Checked Out/Retired
    if (filters.status !== 'all') {
      if (filters.status === 'Available') {
        // Available = checkout_status is 'available' OR (no assigned_to and not retired/in repair)
        const isAvailable = a.checkout_status === 'available' || 
          (!a.assigned_to && a.status !== 'Retired' && a.status !== 'In Repair');
        if (!isAvailable) return false;
      } else if (filters.status === 'Checked Out') {
        // Checked Out = has assigned_to or checkout_status is 'checked_out' (and not retired)
        const isCheckedOut = (a.checkout_status === 'checked_out' || 
          (a.assigned_to && a.assigned_to !== '' && a.assigned_to !== 'None')) && a.status !== 'Retired';
        if (!isCheckedOut) return false;
      } else if (filters.status === 'Retired') {
        if (a.status !== 'Retired') return false;
      } else if (a.effective_status !== filters.status) {
        return false;
      }
    }
    if (filters.department !== 'all' && a.department !== filters.department) return false;
    if (filters.type !== 'all' && a.asset_type !== filters.type) return false;
    if (filters.source === 'action1' && !a.action1_id) return false;
    if (filters.source === 'manual' && a.source !== 'manual') return false;
    
    return true;
  });

  const exportCSV = () => {
    const headers = ['Name', 'Type', 'Serial', 'Manufacturer', 'Model', 'OS', 'Status', 'IP', 'Department', 'Assigned To', 'Source'];
    const rows = filteredAssets.map(a => [
      a.name, a.asset_type, a.serial, a.manufacturer, a.model, a.os,
      a.effective_status, a.ip_address, a.department, a.assigned_to,
      a.action1_id ? 'Action1' : 'Manual'
    ].map(v => `"${v || ''}"`).join(','));
    
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assets_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const AssetTypeIcon = ({ type }) => {
    const Icon = ASSET_TYPE_ICONS[type] || Monitor;
    return <Icon className="h-4 w-4" />;
  };

  return (
    <div data-testid="assets-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Asset Inventory</h1>
          <div className="flex items-center gap-2 text-sm mt-1">
            {syncStatus?.action1_connected ? (
              <span className="flex items-center gap-1 text-green-600">
                <Cloud className="h-3.5 w-3.5" /> Action1 Connected
              </span>
            ) : (
              <span className="flex items-center gap-1 text-gray-400">
                <CloudOff className="h-3.5 w-3.5" /> Action1 Offline
              </span>
            )}
            <span className="text-gray-300">|</span>
            <span className="flex items-center gap-1 text-slate-500">
              <Database className="h-3.5 w-3.5" /> {syncStatus?.total_assets || 0} in DB
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setShowAddDialog(true)} className="gap-2" data-testid="add-asset-btn">
            <Plus className="h-4 w-4" />
            Add Asset
          </Button>
          {assets.length === 0 && syncStatus?.action1_connected && (
            <Button variant="default" size="sm" onClick={handleImport} disabled={syncing} className="gap-2 bg-purple-600 hover:bg-purple-700">
              <Download className="h-4 w-4" />
              Import from Action1
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => handleSync(true)} disabled={syncing} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            Sync
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV} className="gap-2">
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setFilters(f => ({ ...f, status: 'all', source: 'all' }))}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-xs text-gray-500">Total Assets</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setFilters(f => ({ ...f, status: 'Online' }))}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.online}</p>
              <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                <Wifi className="h-3 w-3" /> Online
              </p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setFilters(f => ({ ...f, status: 'Offline' }))}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-500">{stats.offline}</p>
              <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                <WifiOff className="h-3 w-3" /> Offline
              </p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md border-purple-200" onClick={() => setFilters(f => ({ ...f, source: 'action1' }))}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{stats.action1}</p>
              <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                <Cloud className="h-3 w-3" /> Action1
              </p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md border-blue-200" onClick={() => setFilters(f => ({ ...f, source: 'manual' }))}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.manual}</p>
              <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                <Database className="h-3 w-3" /> Manual
              </p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md border-orange-200" onClick={() => navigate('/assets/reports')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-orange-600">{stats.needsUpdates}</p>
              <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Updates
              </p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.needsReboot}</p>
              <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                <RotateCcw className="h-3 w-3" /> Reboot
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Inventory Summary Table */}
        {inventory.length > 0 && (
          <Card className="mt-6">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Package className="h-5 w-5 text-purple-600" />
                Inventory Summary
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Asset Type</th>
                      <th className="text-center py-2 px-3 font-semibold text-slate-600">Total</th>
                      <th className="text-center py-2 px-3 font-semibold text-green-600">Available</th>
                      <th className="text-center py-2 px-3 font-semibold text-blue-600">Checked Out</th>
                      <th className="text-center py-2 px-3 font-semibold text-orange-600">In Repair</th>
                      <th className="text-center py-2 px-3 font-semibold text-slate-400">Retired</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventory.map((item, idx) => (
                      <tr 
                        key={item.type} 
                        className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                        onClick={() => setFilters(f => ({ ...f, type: item.type }))}
                      >
                        <td className="py-2.5 px-3 font-medium text-slate-800 flex items-center gap-2">
                          <AssetTypeIcon type={item.type} />
                          {item.type}
                        </td>
                        <td className="text-center py-2.5 px-3 font-bold text-slate-700">{item.total}</td>
                        <td className="text-center py-2.5 px-3">
                          <span className={`inline-block min-w-[28px] px-2 py-0.5 rounded text-xs font-bold ${item.available > 0 ? 'bg-green-100 text-green-700' : 'text-slate-400'}`}>
                            {item.available}
                          </span>
                        </td>
                        <td className="text-center py-2.5 px-3">
                          <span className={`inline-block min-w-[28px] px-2 py-0.5 rounded text-xs font-bold ${item.checked_out > 0 ? 'bg-blue-100 text-blue-700' : 'text-slate-400'}`}>
                            {item.checked_out}
                          </span>
                        </td>
                        <td className="text-center py-2.5 px-3">
                          <span className={`inline-block min-w-[28px] px-2 py-0.5 rounded text-xs font-bold ${item.in_repair > 0 ? 'bg-orange-100 text-orange-700' : 'text-slate-400'}`}>
                            {item.in_repair}
                          </span>
                        </td>
                        <td className="text-center py-2.5 px-3">
                          <span className={`inline-block min-w-[28px] px-2 py-0.5 rounded text-xs font-bold ${item.retired > 0 ? 'bg-slate-200 text-slate-600' : 'text-slate-400'}`}>
                            {item.retired}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="bg-slate-100 font-semibold">
                      <td className="py-2.5 px-3 text-slate-800">TOTAL</td>
                      <td className="text-center py-2.5 px-3 text-slate-800">{inventory.reduce((sum, i) => sum + i.total, 0)}</td>
                      <td className="text-center py-2.5 px-3 text-green-700">{inventory.reduce((sum, i) => sum + i.available, 0)}</td>
                      <td className="text-center py-2.5 px-3 text-blue-700">{inventory.reduce((sum, i) => sum + i.checked_out, 0)}</td>
                      <td className="text-center py-2.5 px-3 text-orange-700">{inventory.reduce((sum, i) => sum + i.in_repair, 0)}</td>
                      <td className="text-center py-2.5 px-3 text-slate-600">{inventory.reduce((sum, i) => sum + i.retired, 0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State - No Assets */}
        {!loading && assets.length === 0 && (
          <Card className="border-dashed border-2 border-gray-300">
            <CardContent className="p-12 text-center">
              <Database className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No Assets Yet</h3>
              <p className="text-sm text-gray-500 mb-6">
                Your asset database is empty. Import devices from Action1 or add them manually.
              </p>
              <div className="flex justify-center gap-4">
                {syncStatus?.action1_connected && (
                  <Button onClick={handleImport} disabled={syncing} className="gap-2">
                    <Cloud className="h-4 w-4" />
                    Import from Action1
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowAddDialog(true)} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Add Manually
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        {assets.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search assets..."
                      className="pl-10"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      data-testid="search-assets-input"
                    />
                  </div>
                </div>
                <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Available">Available</SelectItem>
                    <SelectItem value="Checked Out">Checked Out</SelectItem>
                    <SelectItem value="Online">Online</SelectItem>
                    <SelectItem value="Offline">Offline</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Retired">Retired</SelectItem>
                    <SelectItem value="In Repair">In Repair</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.type} onValueChange={(v) => setFilters(f => ({ ...f, type: v }))}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {assetTypes.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filters.department} onValueChange={(v) => setFilters(f => ({ ...f, department: v }))}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filters.source} onValueChange={(v) => setFilters(f => ({ ...f, source: v }))}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="action1">Action1</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
                {(filters.status !== 'all' || filters.department !== 'all' || filters.type !== 'all' || filters.source !== 'all') && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setFilters({ status: 'all', department: 'all', type: 'all', source: 'all' })}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
              {/* Bulk Actions Bar */}
              {selectedAssets.length > 0 && (
                <div className="flex items-center justify-between bg-purple-50 p-3 rounded-lg mt-4 border border-purple-200">
                  <span className="text-sm font-medium text-purple-700">
                    {selectedAssets.length} asset(s) selected
                  </span>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setSelectedAssets([])}
                    >
                      Clear Selection
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleBulkDelete(false)}
                      disabled={deleting}
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                    >
                      Retire Selected
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleBulkDelete(true)}
                      disabled={deleting}
                    >
                      {deleting ? 'Deleting...' : 'Delete Permanently'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Asset List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 mx-auto animate-spin text-purple-600 mb-4" />
            <p className="text-gray-500">Loading assets...</p>
          </div>
        ) : assets.length > 0 && (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="w-10 px-4 py-3">
                        <input 
                          type="checkbox" 
                          checked={selectedAssets.length === filteredAssets.length && filteredAssets.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded border-gray-300"
                        />
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Asset</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Type</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Serial / IP</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Department</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Assigned To</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Source</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredAssets.map(asset => (
                      <tr 
                        key={asset.asset_id} 
                        className={`hover:bg-gray-50 cursor-pointer ${selectedAssets.includes(asset.asset_id) ? 'bg-purple-50' : ''}`}
                        data-testid={`asset-row-${asset.asset_id}`}
                      >
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input 
                            type="checkbox" 
                            checked={selectedAssets.includes(asset.asset_id)}
                            onChange={() => toggleAssetSelection(asset.asset_id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-4 py-3" onClick={() => navigate(`/assets/${asset.asset_id}`)}>
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${asset.effective_status === 'Online' ? 'bg-green-100' : 'bg-gray-100'}`}>
                              <AssetTypeIcon type={asset.asset_type} />
                            </div>
                            <div>
                              <p className="font-medium text-sm text-gray-900">{asset.name}</p>
                              <p className="text-xs text-gray-500">{asset.manufacturer} {asset.model}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3" onClick={() => navigate(`/assets/${asset.asset_id}`)}>
                          <span className="text-sm text-gray-600">{asset.asset_type}</span>
                        </td>
                        <td className="px-4 py-3" onClick={() => navigate(`/assets/${asset.asset_id}`)}>
                          {/* Show Retired status first, then checkout status, then effective_status */}
                          {asset.status === 'Retired' ? (
                            <Badge className="bg-gray-200 text-gray-700">Retired</Badge>
                          ) : asset.checkout_status === 'available' ? (
                            <Badge className="bg-green-100 text-green-700">Available</Badge>
                          ) : asset.checkout_status === 'checked_out' || asset.assigned_to ? (
                            <Badge className="bg-orange-100 text-orange-700">Checked Out</Badge>
                          ) : (
                            <Badge className={STATUS_COLORS[asset.effective_status] || 'bg-gray-100'}>
                              {asset.effective_status}
                            </Badge>
                          )}
                          {asset.action1_reboot_required === 'Yes' && (
                            <Badge className="ml-1 bg-blue-100 text-blue-700 text-xs">
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Reboot
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3" onClick={() => navigate(`/assets/${asset.asset_id}`)}>
                          <p className="text-xs text-gray-600">{asset.serial || '-'}</p>
                          <p className="text-xs text-gray-400">{asset.ip_address || '-'}</p>
                        </td>
                        <td className="px-4 py-3" onClick={() => navigate(`/assets/${asset.asset_id}`)}>
                          <span className="text-sm text-gray-600">{asset.department || '-'}</span>
                        </td>
                        <td className="px-4 py-3" onClick={() => navigate(`/assets/${asset.asset_id}`)}>
                          <span className="text-sm text-gray-600">{asset.assigned_to || '-'}</span>
                        </td>
                        <td className="px-4 py-3" onClick={() => navigate(`/assets/${asset.asset_id}`)}>
                          {asset.action1_id ? (
                            <Badge className="bg-purple-100 text-purple-700 text-xs">
                              <Cloud className="h-3 w-3 mr-1" />
                              Action1
                            </Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">
                              <Database className="h-3 w-3 mr-1" />
                              Manual
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t bg-gray-50 text-sm text-gray-500">
                Showing {filteredAssets.length} of {assets.length} assets
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Asset Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Asset</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Asset Name <span className="text-gray-400 text-xs">(leave blank to auto-generate)</span></Label>
              <Input 
                placeholder="Auto-generated (e.g., Gamer-042, Mon-015)"
                value={newAsset.name}
                onChange={(e) => setNewAsset(a => ({ ...a, name: e.target.value }))}
                data-testid="new-asset-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={newAsset.asset_type} onValueChange={(v) => {
                  if (v === '__custom__') {
                    const custom = window.prompt('Enter custom asset type:');
                    if (custom) setNewAsset(a => ({ ...a, asset_type: custom }));
                  } else {
                    setNewAsset(a => ({ ...a, asset_type: v }));
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Laptop">Laptop</SelectItem>
                    <SelectItem value="Desktop">Desktop</SelectItem>
                    <SelectItem value="Computer">Computer</SelectItem>
                    <SelectItem value="Monitor">Monitor</SelectItem>
                    <SelectItem value="Portable Monitor">Portable Monitor</SelectItem>
                    <SelectItem value="Hub">Hub</SelectItem>
                    <SelectItem value="Dock">Docking Station</SelectItem>
                    <SelectItem value="Keyboard">Keyboard</SelectItem>
                    <SelectItem value="Mouse">Mouse</SelectItem>
                    <SelectItem value="Keyboard/Mouse">Keyboard/Mouse Combo</SelectItem>
                    <SelectItem value="60W Power Supply">60W Power Supply</SelectItem>
                    <SelectItem value="170W Power Supply">170W Power Supply</SelectItem>
                    <SelectItem value="Server">Server</SelectItem>
                    <SelectItem value="Printer">Printer</SelectItem>
                    <SelectItem value="Network">Network Device</SelectItem>
                    <SelectItem value="Phone">Phone</SelectItem>
                    <SelectItem value="Tablet">Tablet</SelectItem>
                    <SelectItem value="Peripheral">Peripheral</SelectItem>
                    <SelectItem value="Software">Software License</SelectItem>
                    <SelectItem value="Furniture">Furniture</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                    <SelectItem value="__custom__">+ Add Custom Type...</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Serial Number</Label>
                <Input 
                  placeholder="Serial #"
                  value={newAsset.serial}
                  onChange={(e) => setNewAsset(a => ({ ...a, serial: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Manufacturer</Label>
                <Input 
                  placeholder="e.g., Dell, HP"
                  value={newAsset.manufacturer}
                  onChange={(e) => setNewAsset(a => ({ ...a, manufacturer: e.target.value }))}
                />
              </div>
              <div>
                <Label>Model</Label>
                <Input 
                  placeholder="Model #"
                  value={newAsset.model}
                  onChange={(e) => setNewAsset(a => ({ ...a, model: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Department</Label>
                <Input 
                  placeholder="Department"
                  value={newAsset.department}
                  onChange={(e) => setNewAsset(a => ({ ...a, department: e.target.value }))}
                />
              </div>
              <div>
                <Label>Location</Label>
                <Input 
                  placeholder="Location"
                  value={newAsset.location}
                  onChange={(e) => setNewAsset(a => ({ ...a, location: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Assigned To</Label>
              <Input 
                placeholder="User name or email"
                value={newAsset.assigned_to}
                onChange={(e) => setNewAsset(a => ({ ...a, assigned_to: e.target.value }))}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Input 
                placeholder="Any additional notes..."
                value={newAsset.notes}
                onChange={(e) => setNewAsset(a => ({ ...a, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddAsset} data-testid="save-new-asset-btn">Add Asset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetsPage;
